import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Plus } from 'lucide-react';
import { formatCents, formatDate } from '@vuokramani/shared';
import { PageHeader, pageStyles as s } from '../../components/page.tsx';
import { buttonClass, Splash } from '../../components/ui.tsx';
import { useProperties } from '../../lib/properties.ts';
import { useTasks } from '../../lib/inspections.ts';
import { monthLabel } from '../../lib/invoices.ts';
import { useThreadCounts } from '../../lib/messages.ts';
import { useFinance } from '../../lib/finance.ts';
import { PropertyRow } from './PropertyRow.tsx';
import { Onboarding } from './Onboarding.tsx';

const LIST_LIMIT = 5;

interface Todo {
  key: string;
  to: string;
  title: string;
  meta?: string;
  tone: 'danger' | 'warn' | 'accent';
}

/**
 * Yhteenveto: ensin se, mikä vaatii toimia, sitten vuoden luvut ja asunnot.
 * Tyhjiä nollia ei näytetä.
 */
export function OverviewPage() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const { data: properties, isLoading } = useProperties('owner');
  const has = Boolean(properties?.length);
  const { data: tasks } = useTasks(has);
  const { data: counts } = useThreadCounts('owner', has);
  const { data: finance } = useFinance(year);
  const money = (c: number) => formatCents(c, i18n.language);

  if (isLoading) return <Splash />;

  const addButton = (
    <Link to="/vuokranantaja/asunnot/uusi" className={buttonClass('primary')}>
      <Plus size={17} aria-hidden="true" />
      {t('properties.add')}
    </Link>
  );

  if (!properties || properties.length === 0) {
    return (
      <div className={s.stack}>
        <PageHeader title={t('overview.title')} />
        <Onboarding properties={[]} />
      </div>
    );
  }

  const todos: Todo[] = [];

  // Erääntyneet vuokrat asunnoittain
  const overdue = new Map<
    string,
    { title: string; count: number; cents: number; oldest: string }
  >();
  for (const o of finance?.overdue ?? []) {
    const g = overdue.get(o.propertyId) ?? {
      title: o.propertyTitle,
      count: 0,
      cents: 0,
      oldest: o.dueDate,
    };
    g.count += 1;
    g.cents += o.openCents;
    if (o.dueDate < g.oldest) g.oldest = o.dueDate;
    overdue.set(o.propertyId, g);
  }
  for (const [propertyId, g] of overdue) {
    todos.push({
      key: `overdue-${propertyId}`,
      to: `/vuokranantaja/asunnot/${propertyId}/talous`,
      title: t('financePage.overdueRow', { property: g.title, count: g.count }),
      meta: t('financePage.overdueMeta', {
        amount: money(g.cents),
        date: formatDate(g.oldest, i18n.language),
      }),
      tone: 'danger',
    });
  }

  for (const task of tasks ?? []) {
    if (task.type === 'deposit_pending') {
      todos.push({
        key: `deposit-${task.leaseId}`,
        to: `/vuokranantaja/asunnot/${task.propertyId}/vuokrasuhde`,
        title: t('overview.taskDeposit', { property: task.propertyTitle }),
        meta: t('overview.taskDepositMeta', { date: formatDate(task.endDate, i18n.language) }),
        tone: 'warn',
      });
    } else if (task.type === 'invoice_reported') {
      todos.push({
        key: task.invoiceId,
        to: `/vuokranantaja/asunnot/${task.propertyId}/talous`,
        title: t('overview.taskPayment', {
          property: task.propertyTitle,
          month: monthLabel(task.period, i18n.language),
        }),
        meta: t('overview.taskPaymentMeta', {
          name: task.tenantName ?? '',
          date: formatDate(task.paidOn, i18n.language),
        }),
        tone: 'accent',
      });
    } else {
      todos.push({
        key: `${task.propertyId}-${task.inspectionType}`,
        to: `/vuokranantaja/asunnot/${task.propertyId}/tarkastus`,
        title: t(
          task.inspectionType === 'move_out' ? 'overview.taskMoveOut' : 'overview.taskInspection',
          { property: task.propertyTitle },
        ),
        meta: t('overview.taskInspectionMeta', {
          name: task.tenantName ?? '',
          date: formatDate(task.at, i18n.language),
        }),
        tone: 'accent',
      });
    }
  }

  if (counts?.openFaults) {
    todos.push({
      key: 'faults',
      to: '/vuokranantaja/viestit',
      title: t('overview.faults', { count: counts.openFaults }),
      tone: 'warn',
    });
  }

  const income = (finance?.properties ?? []).reduce((n, p) => n + p.incomeCents, 0);
  const expenses = (finance?.properties ?? []).reduce((n, p) => n + p.expenseCents, 0);

  return (
    <div className={s.stack}>
      <PageHeader title={t('overview.title')} actions={addButton} />

      <Onboarding properties={properties} />

      <section>
        <h2 className={s.sectionTitle}>{t('overview.tasks')}</h2>
        {todos.length === 0 ? (
          <p className={s.allClear}>{t('overview.allClear')}</p>
        ) : (
          <ul className={s.propertyList}>
            {todos.map((todo) => (
              <li key={todo.key}>
                <Link to={todo.to} className={s.propertyRow}>
                  <span
                    className={`${s.dot} ${todo.tone === 'danger' ? s.dotDanger : todo.tone === 'warn' ? s.dotWarn : ''}`}
                    aria-hidden="true"
                  />
                  <span className={s.propertyText}>
                    <span className={s.propertyName}>{todo.title}</span>
                    {todo.meta && <span className={s.propertyMeta}>{todo.meta}</span>}
                  </span>
                  <span className={s.propertyAside}>
                    <ChevronRight size={18} aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>{t('overview.year', { year })}</h2>
          <Link to="/vuokranantaja/talous">{t('overview.toFinance')}</Link>
        </div>
        {income === 0 && expenses === 0 ? (
          <p className={s.allClear}>{t('overview.yearEmpty', { year })}</p>
        ) : (
          <dl className={s.figures}>
            <div>
              <dt>{t('overview.income')}</dt>
              <dd>{money(income)}</dd>
            </div>
            <div>
              <dt>{t('overview.expenses')}</dt>
              <dd>{money(expenses)}</dd>
            </div>
            <div>
              <dt>{t('overview.difference')}</dt>
              <dd>{money(income - expenses)}</dd>
            </div>
          </dl>
        )}
      </section>

      <section>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>{t('overview.properties')}</h2>
          {properties.length > LIST_LIMIT && (
            <Link to="/vuokranantaja/asunnot">{t('overview.seeAll')}</Link>
          )}
        </div>
        <ul className={s.propertyList}>
          {properties.slice(0, LIST_LIMIT).map((p) => (
            <li key={p.id}>
              <PropertyRow property={p} to={`/vuokranantaja/asunnot/${p.id}`} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
